import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { deleteUser, getAllUsers } from "../../../../services/userServices";
import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_FILTERS,
  buildUserStats,
  filterUsers,
  paginateItems,
  sortUsers,
} from "../utils/adminUsersUtils";

export function useAdminUsers() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const fetchUsers = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getAllUsers();
      const safeUsers = Array.isArray(data) ? data : [];

      setUsers(safeUsers);

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Utilisateurs actualisés.", "success");
      }
    } catch (error) {
      console.error("Erreur récupération utilisateurs :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des utilisateurs.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers({ silent: true });
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    return sortUsers(filterUsers(users, filters), filters.sort);
  }, [filters, users]);

  const pagination = useMemo(() => {
    return paginateItems(filteredUsers, page, filters.perPage);
  }, [filteredUsers, filters.perPage, page]);

  const stats = useMemo(() => {
    return buildUserStats(users);
  }, [users]);

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const openUserDrawer = useCallback((user) => {
    setSelectedUser(user);
    setIsDrawerOpen(true);
  }, []);

  const closeUserDrawer = useCallback(() => {
    setSelectedUser(null);
    setIsDrawerOpen(false);
  }, []);

  const handleDeleteUser = useCallback(async (user) => {
    if (!user?.id) return;

    const confirmDelete = window.confirm(
      `Supprimer l'utilisateur ${user.prenom || ""} ${user.nom || ""} ?`,
    );

    if (!confirmDelete) return;

    setDeletingId(user.id);

    try {
      await deleteUser(user.id);

      setUsers((prev) => prev.filter((item) => item.id !== user.id));

      notificationRef.current?.("Utilisateur supprimé avec succès.", "success");

      if (selectedUser?.id === user.id) {
        closeUserDrawer();
      }
    } catch (error) {
      console.error("Erreur suppression utilisateur :", error);

      notificationRef.current?.(
        "Erreur lors de la suppression de l'utilisateur.",
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  }, [closeUserDrawer, selectedUser]);

  return {
    users,
    filteredUsers,
    paginatedUsers: pagination.items,
    filters,
    stats,
    selectedUser,
    isDrawerOpen,
    loading,
    refreshing,
    deletingId,
    page: pagination.page,
    totalPages: pagination.totalPages,
    fetchUsers,
    updateFilter,
    resetFilters,
    setPage,
    openUserDrawer,
    closeUserDrawer,
    handleDeleteUser,
  };
}